/* KitchenIntelligenceTab — KOT times, station workload, rush-hour heatmap. */
import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Clock, Flame, ChefHat } from 'lucide-react';

const DOW_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function KitchenIntelligenceTab({ outletId }) {
  const [kot, setKot] = useState(null);
  const [rush, setRush] = useState(null);
  const [station, setStation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [k, r, s] = await Promise.all([
          api.get(`/outlet-intel/${outletId}/kitchen/kot-times?days=7`),
          api.get(`/outlet-intel/${outletId}/kitchen/rush-heatmap?days=7`),
          api.get(`/outlet-intel/${outletId}/kitchen/station-workload?days=7`),
        ]);
        if (cancelled) return;
        setKot(k.data); setRush(r.data); setStation(s.data);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [outletId]);

  if (loading) return <div className="text-sm" style={{ color: '#6B7280' }}>Loading kitchen intelligence…</div>;

  const o = kot?.overall || {};

  return (
    <div className="space-y-4" data-testid="kitchen-intel">
      {/* KOT time KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Order → KOT printed" value={`${o.kot_print_min || 0} min`} sub="Time on counter" testid="kpi-kot-print" icon={Clock} />
        <Kpi label="KOT → Food ready" value={`${o.prep_min || 0} min`} sub="Kitchen throughput" testid="kpi-prep" icon={ChefHat} highlight />
        <Kpi label="Ready → Served" value={`${o.serve_min || 0} min`} sub="Service speed" testid="kpi-serve" icon={Clock} />
        <Kpi label="Total cycle" value={`${o.total_min || 0} min`} sub={`${o.orders || 0} orders / 7d`} testid="kpi-total" icon={Flame} />
      </div>

      {/* By meal period */}
      <Card title="KOT Times by Meal Period" testid="card-by-period">
        <div className="grid grid-cols-4 gap-3">
          {['breakfast','lunch','dinner','off_peak'].map(p => {
            const b = kot?.by_meal_period?.[p] || {};
            return (
              <div key={p} className="rounded-lg p-3" style={{ backgroundColor: '#F7F5F2' }}>
                <p className="text-[10px] uppercase font-semibold" style={{ color: '#6B7280' }}>{p.replace('_',' ')}</p>
                <p className="text-lg font-semibold mt-1" style={{ color: '#1B2A4A' }}>{b.prep_min || 0} <span className="text-[11px] font-normal">min prep</span></p>
                <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>{b.orders || 0} orders · total {b.total_min || 0} min</p>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Station workload */}
      <Card title="Station Workload (7d)" testid="card-station">
        {(station?.rows || []).map(r => (
          <div key={r.station} className="py-2 flex items-center gap-3 text-[12px]">
            <div className="flex-1">
              <div className="flex justify-between">
                <span className="font-medium">{r.station}</span>
                <span>{r.items_made} items · <span style={{ color: '#6B7280' }}>{r.items_per_day}/day</span></span>
              </div>
              <p className="text-[10px]" style={{ color: '#6B7280' }}>Category: {r.category}</p>
            </div>
          </div>
        ))}
      </Card>

      {/* Rush heatmap — top 30 busiest 15-min slots */}
      <Card title={`Rush-Hour Heatmap — Top 30 Busiest 15-min Windows (7d)`} testid="card-rush">
        <p className="text-[11px] mb-3" style={{ color: '#6B7280' }}>Peak avg: ~{rush?.hottest_avg_orders || 0} orders per slot · deploy extra hands.</p>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          {(rush?.top_30_rush_slots || []).slice(0, 15).map(r => (
            <div key={`${r.dow}_${r.slot}`} className="rounded p-2 text-center text-[11px]" style={{
              backgroundColor: `rgba(245, 158, 11, ${Math.min(r.count / (rush?.top_30_rush_slots?.[0]?.count || 1), 1)})`,
              color: r.count > 5 ? 'white' : '#1B2A4A',
            }}>
              <div className="font-semibold">{DOW_LABELS[r.dow]}</div>
              <div>{String(r.hour).padStart(2,'0')}:{String(r.minute).padStart(2,'0')}</div>
              <div className="text-[10px] opacity-90">{r.count} orders</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub, highlight, icon: Icon, testid }) {
  return (
    <div className="rounded-xl p-4" style={{
      backgroundColor: highlight ? '#1B2A4A' : 'white',
      color: highlight ? 'white' : '#1B2A4A',
      border: highlight ? 'none' : '1px solid #E5E1DC',
    }} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={12} color={highlight ? '#9DB5CC' : '#6B7280'} />}
        <p className="text-[10px] uppercase tracking-wider" style={{ color: highlight ? '#9DB5CC' : '#6B7280' }}>{label}</p>
      </div>
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-[11px] mt-1" style={{ color: highlight ? '#9DB5CC' : '#6B7280' }}>{sub}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl bg-white p-5" style={{ border: '1px solid #E5E1DC' }} data-testid={testid}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>{title}</h3>
      {children}
    </div>
  );
}
